import { useEffect, useRef } from "react";

/**
 * Fixed video background that plays behind the entire site.
 * Uses an explicit seamless-loop strategy: short-keyframe-interval WebM source
 * + manual `ended`/timeupdate restart so there is no perceptible freeze at the
 * loop boundary. A Ken-Burns CSS animation overlays continuous flow even at
 * the wrap-around frame.
 */
export default function VideoBackground() {
  const videoRef = useRef(null);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    v.playbackRate = 0.5; // slow flow — half speed for longer perceived duration
    v.muted = true;

    let tries = 0;
    const tryPlay = () => {
      const p = v.play();
      if (p && typeof p.catch === "function") {
        p.catch(() => {
          if (++tries < 4) setTimeout(tryPlay, 400);
        });
      }
    };
    tryPlay();

    // 1) ended → instant restart
    const onEnded = () => {
      v.currentTime = 0;
      tryPlay();
    };
    // 2) belt-and-suspenders: if we're within 0.15s of duration, hop back to 0 early
    //    (avoids the tiny stall some browsers add between loops)
    const onTimeUpdate = () => {
      if (!v.duration || !isFinite(v.duration)) return;
      if (v.duration - v.currentTime < 0.05) {
        v.currentTime = 0;
        tryPlay();
      }
    };
    // 3) If browser fires a stall/pause, kick it again
    const onStalled = () => tryPlay();
    const onPause = () => {
      // never let it stay paused
      if (!document.hidden) tryPlay();
    };

    v.addEventListener("ended", onEnded);
    v.addEventListener("timeupdate", onTimeUpdate);
    v.addEventListener("stalled", onStalled);
    v.addEventListener("pause", onPause);

    // Resume when tab becomes visible again
    const onVis = () => { if (!document.hidden) tryPlay(); };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      v.removeEventListener("ended", onEnded);
      v.removeEventListener("timeupdate", onTimeUpdate);
      v.removeEventListener("stalled", onStalled);
      v.removeEventListener("pause", onPause);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  return (
    <div
      data-testid="video-background"
      className="pointer-events-none fixed inset-0 z-0 overflow-hidden bg-[#030508]"
      aria-hidden
    >
      <video
        ref={videoRef}
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
        // disablePictureInPicture
        className="absolute inset-0 w-full h-full object-cover dna-flow"
        style={{ filter: "saturate(1.4) brightness(1.3) contrast(1.05)" }}
      >
        <source src="/assets/dna-bg.webm" type="video/webm" />
        <source src="/assets/dna-bg.mp4" type="video/mp4" />
      </video>
      {/* Light overlay so text remains readable */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(3,5,8,0.25) 0%, rgba(3,5,8,0.4) 50%, rgba(3,5,8,0.55) 100%)",
        }}
      />
    </div>
  );
}
